import { FC, MutableRefObject, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useUnit } from 'effector-react';
import { IconButton, Skeleton, Tooltip, Typography } from '@mui/material';
import AodOutlinedIcon from '@mui/icons-material/AodOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PodcastsOutlinedIcon from '@mui/icons-material/PodcastsOutlined';
import SupportOutlinedIcon from '@mui/icons-material/SupportOutlined';
import { Avatar } from '@features/socket-chats-reworked/components/avatar';
import {
  DrawerContact,
  DrawerContactAddress,
  DrawerContactInfo,
  DrawerContactMeta,
  DrawerContactName,
  DrawerContacts,
  DrawerContainer,
  DrawerContent,
  DrawerHeader,
  DrawerItem,
  DrawerItemHeader,
  DrawerSkeletons,
  DrawerTitle,
  DrawerItemHeaderTitle,
} from '@features/socket-chats-reworked/components/chat/chat-drawer/styled';
import {
  $chatData,
  $contactsByChannels,
  $currentChatId,
  $isChatLoading,
  $isDrawerVisible,
  setDrawerVisibility,
  changeVisibilityAddContactsModal,
} from '@features/socket-chats-reworked/models';
import type { ContactInstance } from '@features/socket-chats-reworked/types/contact';
import {
  getContactDisplayName,
  sortContactsByName,
} from '@features/socket-chats-reworked/lib/contacts';
import { ActionButton } from '@ui/index';
import { ChatAddContacts } from '../chat-add-contacts';

export const ChatDrawer: FC<{ container: MutableRefObject<null> | null }> = ({
  container,
}) => {
  const { t } = useTranslation();
  const isDrawerVisible = useUnit($isDrawerVisible);
  const chatData = useUnit($chatData);
  const chatId = useUnit($currentChatId);
  const contacts = useUnit($contactsByChannels);
  const isChatLoading = useUnit($isChatLoading);

  const chatContacts = contacts[chatId!] || null;
  const chatContactsLength = chatContacts ? Object.keys(chatContacts).length : null;
  const sortedChatContacts = useMemo(
    () => (chatContacts ? sortContactsByName(Object.values(chatContacts) as ContactInstance[]) : []),
    [chatContacts],
  );
  const isContactsLoading = isChatLoading && !chatContactsLength;

  const handleCloseClick = () => {
    setDrawerVisibility(false);
  };

  const handleAddContacts = () => changeVisibilityAddContactsModal(true);

  const renderAvatar = (contact: ContactInstance) => {
    const title = contact ? contact.avatar?.title : chatData?.avatar?.title;
    const color = contact ? contact.avatar?.color : chatData?.avatar?.color;

    return <Avatar size="medium" title={title} color={color} />;
  };

  const renderRole = (role: string) => {
    switch (role) {
      case 'BOT':
        return (
          <Tooltip title={t('Bot')}>
            <PodcastsOutlinedIcon />
          </Tooltip>
        );
      case 'support':
        return (
          <Tooltip title={t('TechSupport')}>
            <SupportOutlinedIcon />
          </Tooltip>
        );
      case 'mc_manager':
        return (
          <Tooltip title={t('ManagementCE')}>
            <BadgeOutlinedIcon />
          </Tooltip>
        );
      case 'builder_manager':
        return (
          <Tooltip title={t('Management')}>
            <AodOutlinedIcon />
          </Tooltip>
        );
      default:
        return null;
    }
  };

  const renderContacts = () => {
    if (!chatContacts) return null;
    return sortedChatContacts.map(
      (contact: ContactInstance): JSX.Element => (
        <DrawerContact key={contact.id}>
          {renderAvatar(contact)}

          <DrawerContactInfo>
            <DrawerContactName>{getContactDisplayName(contact)}</DrawerContactName>
            <DrawerContactAddress>{contact.address}</DrawerContactAddress>
          </DrawerContactInfo>

          <DrawerContactMeta>{renderRole(contact.role)}</DrawerContactMeta>
        </DrawerContact>
      )
    );
  };

  return (
    <>
      <ChatAddContacts container={container} />

      <DrawerContainer isVisible={isDrawerVisible}>
        <DrawerHeader>
          <DrawerTitle>{t('ChatInformation')}</DrawerTitle>
          <IconButton size="medium" onClick={handleCloseClick}>
            <CloseRoundedIcon />
          </IconButton>
        </DrawerHeader>

        <DrawerContent>
          {isContactsLoading ? (
            <DrawerSkeletons>
              {[1, 2, 3, 4, 5].map((item: number) => (
                <DrawerContact key={item}>
                  <Skeleton variant="circular" width={54} height={54} />
                  <Skeleton variant="rounded" width={210} height={20} />
                </DrawerContact>
              ))}
            </DrawerSkeletons>
          ) : (
            <DrawerItem>
              <DrawerItemHeader>
                <DrawerItemHeaderTitle>
                  <PeopleAltOutlinedIcon />
                  <Typography variant="subtitle2">
                    <span>{t('Participants')}</span>
                    <span>: </span>
                    <span>{chatContactsLength}</span>
                  </Typography>
                </DrawerItemHeaderTitle>
                <ActionButton onClick={handleAddContacts}>{t('add')}</ActionButton>
              </DrawerItemHeader>

              <DrawerContacts>{renderContacts()}</DrawerContacts>
            </DrawerItem>
          )}
        </DrawerContent>
      </DrawerContainer>
    </>
  );
};
